import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface Track {
  name: string;
  state: string;
  type: string;
}

export interface DataCompleteness {
  has_odds: boolean;
  races_with_odds: number;
  total_races: number;
  has_form_data: boolean;
  has_track_conditions: boolean;
}

export interface AnalysisStatus {
  normal_analysis_races: number;
  normal_analysis_complete: boolean;
  value_analysis_races: number;
  value_analysis_complete: boolean;
  total_races: number;
}

export interface TrackConditionDetails {
  track_condition: string | null;
  track_condition_number: string | null;
  rail_position: string | null;
  weather: string | null;
  /** Meeting abandoned (members' site greys it out and drops its upcoming tips). */
  abandoned?: boolean;
}

/** Predicted-vs-official going, sent only when the two disagree (see changeset 0034). */
export interface ConditionDrift {
  predicted_track_condition: string | null;
  predicted_track_condition_number: number | null;
  prediction_date: string | null;
  /** Bands moved since the prediction: > 0 wetter, < 0 drying. */
  grade_change: number;
  is_deteriorating: boolean;
}

export type NoteType = 'info' | 'warning' | 'danger';

export interface Meeting {
  id: number;
  meeting_id: string;
  track_id: string;
  meeting_date: string;
  race_count: number | null;
  status: string | null;
  is_private: boolean;
  is_training: boolean;
  platinumiq_private: boolean;
  meeting_note: string | null;
  meeting_note_type: NoteType | null;
  notes: string | null;
  track: Track;
  data_completeness?: DataCompleteness;
  analysis_status?: AnalysisStatus;
  track_condition_details?: TrackConditionDetails | null;
  condition_drift?: ConditionDrift | null;
}

interface CompletenessRow {
  meeting_id: string;
  data_completeness: DataCompleteness;
  analysis_status: AnalysisStatus;
  track_condition_details: TrackConditionDetails | null;
  condition_drift: ConditionDrift | null;
}

/**
 * Merges the plain meetings list with the per-date completeness/readiness endpoint,
 * matching the old admin's two-call fetch (docker/admin/src/app/meetings/meetings-list.tsx)
 * — GET /meetings has no data_completeness/analysis_status, only GET /meetings/data-completeness does.
 */
export function useMeetings(date: string) {
  return useQuery({
    queryKey: ['meetings', date],
    queryFn: async (): Promise<Meeting[]> => {
      const [meetings, completeness] = await Promise.all([
        apiClient<Meeting[]>(`/meetings?date=${date}`),
        apiClient<CompletenessRow[]>(`/meetings/data-completeness?date=${date}`),
      ]);
      const byId = new Map(completeness.map((c) => [c.meeting_id, c]));
      return meetings.map((meeting): Meeting => {
        const c = byId.get(meeting.meeting_id);
        return {
          ...meeting,
          data_completeness: c?.data_completeness,
          analysis_status: c?.analysis_status,
          track_condition_details: c?.track_condition_details ?? null,
          condition_drift: c?.condition_drift ?? null,
        };
      });
    },
  });
}

export interface MeetingDetail {
  id: number;
  meeting_id: string;
  track_id: string;
  meeting_date: string;
  race_count: number | null;
  status: string | null;
  is_private: boolean;
  is_training: boolean;
  platinumiq_private: boolean;
  track: Track;
}

/** GET /meetings/:meetingId — a single meeting, e.g. for the race drill-down page header. */
export function useMeetingDetail(meetingId: string) {
  return useQuery({
    queryKey: ['meeting', meetingId],
    queryFn: () => apiClient<MeetingDetail>(`/meetings/${meetingId}`),
  });
}

function useInvalidateMeetings() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['meetings'] });
}

interface VisibilityInput {
  meetingId: string;
  isPrivate?: boolean;
  isTraining?: boolean;
  platinumiqPrivate?: boolean;
}

export function useSetMeetingVisibility() {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: ({ meetingId, ...body }: VisibilityInput) =>
      apiClient(`/meetings/${meetingId}/visibility`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: invalidate,
  });
}

interface NoteInput {
  meetingId: string;
  note: string | null;
  noteType: NoteType;
}

export function useSetMeetingNote() {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: ({ meetingId, note, noteType }: NoteInput) =>
      apiClient(`/meetings/${meetingId}/notes`, {
        method: 'PATCH',
        body: JSON.stringify({ note, note_type: noteType }),
      }),
    onSuccess: invalidate,
  });
}

interface AdminNoteInput {
  meetingId: string;
  notes: string | null;
}

export function useSetMeetingAdminNote() {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: ({ meetingId, notes }: AdminNoteInput) =>
      apiClient(`/meetings/${meetingId}/admin-notes`, { method: 'PATCH', body: JSON.stringify({ notes }) }),
    onSuccess: invalidate,
  });
}

export interface PublishResult {
  success: boolean;
  pickCount: number;
  lockCount: number;
  valueCount: number;
}

export function usePublishToClub() {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: (meetingId: string) =>
      apiClient<PublishResult>(`/meetings/${meetingId}/publish-to-club`, {
        method: 'POST',
        body: JSON.stringify({ triggerSource: 'admin_button' }),
      }),
    onSuccess: invalidate,
  });
}

// Full actual-track-conditions row (null until one is entered). The editable
// subset is what the conditions editor writes.
export interface TrackConditions {
  weather: string | null;
  track_condition: string | null;
  track_condition_number: string | null;
  rail_position: string | null;
  penetrometer: number | string | null;
  rainfall: string | null;
  comment: string | null;
  source: string | null;
  abandoned: boolean | null;
}

export type TrackConditionsDraft = Partial<TrackConditions>;

/** GET /meetings/:meetingId/track-conditions — prefill for the conditions editor. */
export function useTrackConditions(meetingId: string, enabled = true) {
  return useQuery({
    queryKey: ['meeting-track-conditions', meetingId],
    queryFn: () => apiClient<TrackConditions | null>(`/meetings/${meetingId}/track-conditions`),
    enabled,
  });
}

export function useSaveTrackConditions(meetingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draft: TrackConditionsDraft) =>
      apiClient<{ success: boolean; trackConditions: TrackConditions }>(`/meetings/${meetingId}/track-conditions`, {
        method: 'PUT',
        body: JSON.stringify(draft),
      }),
    onSuccess: () => {
      // Refresh the list (condition badge + "Conditions" data-check) and the editor's own prefill.
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      queryClient.invalidateQueries({ queryKey: ['meeting-track-conditions', meetingId] });
    },
  });
}

// AI-predicted conditions (produced by the analysis pipeline; editable but not
// creatable by hand — null until a prediction exists).
export interface PredictedConditions {
  predicted_weather: string | null;
  predicted_track_condition: string | null;
  predicted_track_condition_number: string | null;
  predicted_penetrometer: number | string | null;
  prediction_confidence: string | null;
  // The model's rationale for the prediction — read-only, shown so staff can
  // judge how much to trust it.
  ai_reasoning: string | null;
  ai_model: string | null;
  updated_at: string | null;
}

export type PredictedConditionsDraft = Partial<
  Pick<PredictedConditions, 'predicted_weather' | 'predicted_track_condition' | 'predicted_track_condition_number' | 'predicted_penetrometer'>
>;

/** GET /meetings/:meetingId/predicted-conditions — prefill for the predicted-conditions editor. */
export function usePredictedConditions(meetingId: string, enabled = true) {
  return useQuery({
    queryKey: ['meeting-predicted-conditions', meetingId],
    queryFn: () => apiClient<PredictedConditions | null>(`/meetings/${meetingId}/predicted-conditions`),
    enabled,
  });
}

export function useSavePredictedConditions(meetingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draft: PredictedConditionsDraft) =>
      apiClient<{ success: boolean; predictedConditions: PredictedConditions }>(`/meetings/${meetingId}/predicted-conditions`, {
        method: 'PUT',
        body: JSON.stringify(draft),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['meeting-predicted-conditions', meetingId] }),
  });
}

export interface OddsUploadResult {
  success: boolean;
  message: string;
  processed: number;
  total: number;
  unmatched: string[];
}

interface OddsUploadInput {
  meetingId: string;
  raceNumber: number;
  csv: string;
}

/** POST /meetings/:meetingId/odds-upload — one punters.com.au race CSV, sent as text. */
export function useUploadRaceOdds() {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: ({ meetingId, ...body }: OddsUploadInput) =>
      apiClient<OddsUploadResult>(`/meetings/${meetingId}/odds-upload`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: invalidate,
  });
}
